import React from 'react';

export type LiveStatusRegionProps = {
  /** Texto que se anuncia. Cadena vacía = región montada y en silencio. */
  message: string;
};

/**
 * Región de estado accesible (WCAG 4.1.3): anuncia de forma no intrusiva los cambios que la vista
 * no puede comunicar de otra manera (se ha cargado otra página, la lista ha cambiado de filtro,
 * no hay resultados).
 *
 * Dos condiciones la hacen funcionar, y las dos se rompen con facilidad:
 * - **Siempre montada.** Una región que nace junto a su contenido no produce ninguna mutación que
 *   el lector de pantalla pueda detectar, así que no se anuncia. Móntala fuera de los bloques
 *   condicionales de la vista y pásale `''` mientras no haya nada que decir.
 * - **El mensaje es su único contenido.** Una región anuncia cuanto contiene: envolver con ella la
 *   lista o el skeleton haría que el lector leyera la lista entera en cada cambio.
 *
 * Es un `<output>` y no un `<div>`/`<p>` porque es el elemento nativo que ya trae el rol `status`
 * (SonarQube S6819 pide exactamente ese cambio). El rol **no** se escribe además como atributo:
 * sería redundante sobre el elemento nativo y SonarQube S6822 lo marca. Al buscar la región —en un
 * test unitario, en uno e2e o en una auditoría— hazlo por su rol (`getByRole('status')`), que
 * resuelve el implícito, y no por un selector de atributo, que no lo ve.
 *
 * `aria-live="polite"` sí es redundante con el rol y se mantiene a propósito: es la parte del
 * contrato que un lector de pantalla antiguo interpreta sin mapear roles.
 */
function LiveStatusRegion({ message }: Readonly<LiveStatusRegionProps>): JSX.Element {
  return (
    <output aria-live="polite" className="visually-hidden">
      {message}
    </output>
  );
}

export default LiveStatusRegion;
